import * as amplitude from '@amplitude/analytics-browser';

/**
 * Query parameters whose values are secrets or personal data: the reset link's `code` and
 * `email`, the OAuth callback's `code` and `state`, the `id` that grants notification-preference
 * changes (and on /pay, /resubscribe and /review), the forgot-password step's `email`, and the
 * login page's `redirectUrl`, which can hold any of those.
 */
const SECRET_QUERY_PARAMS: ReadonlySet<string> = new Set(['code', 'state', 'email', 'id', 'redirectUrl']);

/** `href` with the values of `SECRET_QUERY_PARAMS` replaced by `REDACTED`, else unchanged. */
export function scrubUrl(href: string): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href.split('?')[0];
  }

  let changed = false;
  for (const key of SECRET_QUERY_PARAMS) {
    if (url.searchParams.has(key)) {
      url.searchParams.set(key, 'REDACTED');
      changed = true;
    }
  }
  return changed ? url.toString() : href;
}

const scrubUrls = (properties: object) => {
  for (const [key, value] of Object.entries(properties)) {
    if (typeof value === 'string' && /^https?:\/\//i.test(value)) {
      Reflect.set(properties, key, scrubUrl(value));
    }
  }
};

/**
 * Scrubs secrets from URLs on every event: page views' `[Amplitude] Page Location` and
 * `referrer`, and attribution's `referrer`/`initial_referrer` user properties. Attribution reads
 * `location.search` itself, so the utm and click-id parameters it records are untouched.
 */
export const urlScrubPlugin: amplitude.Types.EnrichmentPlugin = {
  name: 'UrlScrubPlugin',
  type: 'enrichment',
  execute: async (event) => {
    if (event.event_properties) {
      scrubUrls(event.event_properties);
    }
    // Identify events hold user properties per operation: {$set: {referrer: …}, $setOnce: {…}}.
    for (const operation of Object.values(event.user_properties ?? {})) {
      if (typeof operation === 'object' && operation !== null) {
        scrubUrls(operation);
      }
    }
    return event;
  },
};
